import axios from 'axios';
import {
  CREATE_RISK_FRAMEWORK,
  LIST_RISK_MATRIX_TYPES,
  LIST_RISK_FRAMEWORKS,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import alert_mixin from '@/utils/mixins/alert_mixin';

export default {
  name: 'CreateRiskFramework',
  mixins: [alert_mixin],
  data: () => ({
    loading: false,
    search_query: '',
    filter_mode: 'all',
    sort_mode: 'updated_desc',
    current_page: 1,
    page_size: 10,
    create_form: {
      risk_framework_type_id: '',
      risk_assessment: '',
      assessment_phase: '',
      description: '',
    },
    risk_types: [],
    frameworks: [],
  }),
  computed: {
    filtered_frameworks() {
      const query = String(this.search_query || '').trim().toLowerCase();
      const filter_mode = String(this.filter_mode || 'all');

      return this.frameworks.filter((framework) => {
        if (filter_mode === 'active' && String(framework.status || '').toLowerCase() !== 'active') return false;
        if (filter_mode === 'inactive' && String(framework.status || '').toLowerCase() === 'active') return false;
        if (!query) return true;

        return [
          framework.frameworkId,
          framework.type,
          framework.title,
          framework.description,
          framework.status,
        ].filter(Boolean).join(' ').toLowerCase().includes(query);
      });
    },
    sorted_frameworks() {
      const items = this.filtered_frameworks.slice();
      return items.sort((first, second) => {
        const first_created = new Date(first.date_created || 0).getTime();
        const second_created = new Date(second.date_created || 0).getTime();
        const first_name = String(first.title || '').toLowerCase();
        const second_name = String(second.title || '').toLowerCase();

        if (this.sort_mode === 'name_asc') return first_name.localeCompare(second_name);
        if (this.sort_mode === 'name_desc') return second_name.localeCompare(first_name);
        if (this.sort_mode === 'created_asc') return first_created - second_created;
        return second_created - first_created;
      });
    },
    paginated_frameworks() {
      const start = (this.current_page - 1) * this.page_size;
      return this.sorted_frameworks.slice(start, start + this.page_size);
    },
    total_pages() {
      return Math.max(1, Math.ceil(this.sorted_frameworks.length / this.page_size));
    },
    pagination_label() {
      if (!this.sorted_frameworks.length) return 'Showing 0 results';
      const start = ((this.current_page - 1) * this.page_size) + 1;
      const end = Math.min(this.current_page * this.page_size, this.sorted_frameworks.length);
      return `Showing ${start} to ${end} of ${this.sorted_frameworks.length} results`;
    },
  },
  watch: {
    search_query() { this.current_page = 1; },
    filter_mode() { this.current_page = 1; },
    sort_mode() { this.current_page = 1; },
  },
  created() {
    this.fetch_risk_types();
    this.fetch_risk_frameworks();
  },
  methods: {
    current_project_id() {
      const route_project_id = Number(this.$route?.query?.project_id);
      if (Number.isFinite(route_project_id) && route_project_id > 0) return route_project_id;
      return 1;
    },
    normalize_risk_framework(item) {
    const framework = {
      id: item?.id || Date.now(),
      frameworkId: `FR-${String(item?.id || Date.now()).padStart(3, '0')}`,
      type: item?.risk_framework_type?.risk_type || 'General',
      title: item?.risk_assessment || 'Untitled Risk Framework',
      description: item?.description || '',
      status: item?.is_active ? 'Active' : 'Inactive',
      date_created: item?.date_created || '',
      assessment_phase: item?.assessment_phase || '',
    };
    return framework;

    },
    fetch_risk_types() {
      axios
        .get(LIST_RISK_MATRIX_TYPES, get_request_config())
        .then(({ data }) => {
          if (data.status === 'success') {
            this.risk_types = Array.isArray(data.data) ? data.data : [];
          }
        })
        .catch((error) => {
          this.risk_types = [];
          this.show_error(
            'Error fetching risk types:',
          error?.response?.data?.message || error?.message || error,
          );
        });
    },
    fetch_risk_frameworks() {
      this.loading = true;
      const params = { project_id: this.current_project_id() };

      axios
        .get(LIST_RISK_FRAMEWORKS, get_request_config({ params }))
        .then(({ data }) => {
          if (data.status === 'success') {
            this.frameworks = Array.isArray(data.data)
              ? data.data.map((item) => this.normalize_risk_framework(item))
              : [];
          } else {
            this.frameworks = [];
            this.show_error(data?.message || 'Failed to load risk frameworks.');
          }
        })
        .catch((error) => {
          this.frameworks = [];
          this.show_error(
            error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Failed to load risk frameworks.',
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    format_framework_id(value) {
      return String(value || '').trim();
    },
    open_create_modal() {
      this.create_form = {
        risk_framework_type_id: '',
        risk_assessment: '',
        assessment_phase: '',
        description: '',
      };
    },
    create_risk_framework() {
      const risk_framework_type_id = Number(this.create_form.risk_framework_type_id);
      if (!Number.isFinite(risk_framework_type_id) || risk_framework_type_id <= 0) {
        this.show_error('Please select a risk type.');
        return;
      }

      const risk_assessment = String(this.create_form.risk_assessment || '').trim();
      if (!risk_assessment) {
        this.show_error('Please enter a risk name.');
        return;
      }

      const payload = {
        risk_framework_type_id,
        risk_assessment,
        description: String(this.create_form.description || '').trim(),
        is_active: true,
        project_id: Number(this.$route?.query?.project_id || 1),
        assessment_phase: String(this.create_form.assessment_phase || 'pre').toLowerCase().startsWith('post')
          ? 'post'
          : 'pre',
      };

      axios
        .post(CREATE_RISK_FRAMEWORK, payload, get_request_config())
        .then(({ data }) => {
          if (data.status !== 'success') {
            this.show_error('Error creating risk framework');
            return;
          }

          this.show_success('Risk framework created successfully');
          this.fetch_risk_frameworks();
          this.open_create_modal();
        })
        .catch((error) => {
          this.show_error(
            error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Error creating risk framework',
          );
        });
    },
    set_page(page) {
      if (page < 1 || page > this.total_pages) return;
      this.current_page = page;
    },
    toggle_filter_mode() {
      const next_modes = ['all', 'active', 'inactive'];
      const current_index = next_modes.indexOf(this.filter_mode);
      this.filter_mode = next_modes[(current_index + 1) % next_modes.length];
    },
    cycle_sort_mode() {
      const next_modes = ['updated_desc', 'created_asc', 'name_asc', 'name_desc'];
      const current_index = next_modes.indexOf(this.sort_mode);
      this.sort_mode = next_modes[(current_index + 1) % next_modes.length];
    },
    sort_label() {
      const labels = {
        updated_desc: 'Newest first',
        created_asc: 'Oldest first',
        name_asc: 'Name A-Z',
        name_desc: 'Name Z-A',
      };
      return labels[this.sort_mode] || 'Newest first';
    },
    manage_framework(framework) {
      this.$router.push({
        name: 'RiskCategories',
        query: {
          tab: 'risk_categories',
          risk_framework_assessment_id: String(framework?.id || ''),
        },
      });
    },
    deactivate_framework(framework) {
      void framework;
    },
  },
};
